import { Link, Navigate, useParams } from "react-router-dom";
import {
  ArrowRight,
  ArrowLeft,
  ShieldCheck,
  Users,
  HeartHandshake,
  PhoneCall,
  MessageSquare,
  PlayCircle,
  Compass,
  Check,
} from "lucide-react";
import { INDUSTRIES } from "../../data/industries";
import usePageMeta from "../../lib/usePageMeta";
import AccessModelCallout from "../../components/public/AccessModelCallout";
import BreadcrumbSchema from "../../components/public/BreadcrumbSchema";

const INDUSTRY_VARIANT_MAP = {
  "automotive-dealerships": "industry-automotive",
  "restaurants-hospitality": "industry-restaurants",
  "construction-skilled-trades": "industry-construction",
  "real-estate-brokerages-title-services": "industry-real-estate",
};

/**
 * Industry-specific landing pages under /industries/:slug.
 *
 * All page copy is stored in /app/frontend/src/data/industries.js and is
 * original to California Corporate Chaplains. This component renders the
 * shared page structure — the copy itself varies by industry.
 */
export default function IndustryLandingPage() {
  const { slug } = useParams();
  const data = INDUSTRIES[slug];
  usePageMeta({
    title: data?.meta?.title,
    description: data?.meta?.description,
  });

  if (!data) return <Navigate to="/services" replace />;

  return (
    <div className="bg-white" data-testid={`industry-page-${data.slug}`}>
      <BreadcrumbSchema
        id={`industry-${data.slug}`}
        items={[
          { name: "Home", path: "/" },
          { name: "Industries We Serve", path: "/services" },
          { name: data.name || data.title || "Industry", path: `/industries/${data.slug}` },
        ]}
      />
      {/* HERO */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <Link
          to="/services"
          data-testid="industry-back-services"
          className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#4A6FA5] mb-8"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Services
        </Link>

        <p className="text-xs uppercase tracking-[0.22em] text-[#4A6FA5] font-semibold mb-3" data-testid="industry-eyebrow">
          {data.eyebrow}
        </p>
        <h1
          className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight text-slate-900 leading-[1.05] max-w-4xl"
          data-testid="industry-h1"
        >
          {data.h1}
        </h1>
        <p className="mt-6 text-xl text-slate-700 leading-relaxed max-w-3xl" data-testid="industry-lede">
          {data.lede}
        </p>

        {data.accessLine && (
          <p
            className="mt-4 text-base text-[#4A6FA5] font-medium leading-relaxed max-w-3xl"
            data-testid="industry-access-line"
          >
            {data.accessLine}
          </p>
        )}

        {data.heroImage && (
          <div
            className="mt-10 aspect-[16/9] rounded-2xl overflow-hidden border border-slate-200 shadow-sm bg-slate-100 max-w-4xl"
            data-testid={`industry-hero-image-${data.slug}`}
          >
            <img
              src={data.heroImage.src}
              alt={data.heroImage.alt}
              loading="eager"
              className="w-full h-full object-cover"
            />
          </div>
        )}

        {/* Baseline included panel */}
        <div
          data-testid="industry-baseline-panel"
          className="mt-8 rounded-xl border border-[#4A6FA5]/25 bg-[#F0F4F8] p-5 flex items-start gap-3 max-w-3xl"
        >
          <ShieldCheck className="w-5 h-5 text-[#4A6FA5] shrink-0 mt-0.5" strokeWidth={1.75} />
          <p className="text-sm text-slate-700 leading-relaxed">
            <span className="font-semibold text-slate-900">
              24/7 on-call chaplain access, CALLACHAP, the CCCHAPS Care Portal, and CCCHAPS On Demand are included
            </span>{" "}
            with every CCCHAPS service arrangement. Employees receive access as an employer-provided benefit — they do not pay to use it.
          </p>
        </div>
      </section>

      {/* WHY IT MATTERS */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-slate-900" data-testid="industry-why-title">
          {data.whyItMatters.title}
        </h2>
        <p className="mt-4 text-slate-700 leading-relaxed max-w-3xl">
          {data.whyItMatters.body}
        </p>
        <div className="mt-8 grid md:grid-cols-2 gap-3 max-w-4xl" data-testid="industry-challenges">
          {data.whyItMatters.challenges.map((c, i) => (
            <div
              key={i}
              className="rounded-xl border border-slate-200 bg-white p-4 flex items-start gap-3"
              data-testid={`industry-challenge-${i}`}
            >
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-[#F0F4F8] text-[#4A6FA5] shrink-0 mt-0.5">
                <Compass className="w-3.5 h-3.5" strokeWidth={1.75} />
              </span>
              <p className="text-sm text-slate-700 leading-relaxed">{c}</p>
            </div>
          ))}
        </div>

        <AccessModelCallout
          variant={INDUSTRY_VARIANT_MAP[data.slug] || "default"}
          className="mt-10"
          testId={`industry-access-model-callout-${data.slug}`}
        />
      </section>

      {/* HOW CCCHAPS SUPPORTS */}
      <section className="bg-[#F8FAFC] border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-slate-900" data-testid="industry-how-title">
            {data.howWeSupport.title}
          </h2>
          <div className="mt-6 space-y-4 max-w-3xl">
            {data.howWeSupport.paragraphs.map((p, i) => (
              <p key={i} className="text-slate-700 leading-relaxed">
                {p}
              </p>
            ))}
          </div>
        </div>
      </section>

      {/* ROLE / DEPARTMENT CARDS */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-slate-900" data-testid="industry-roles-title">
          {data.roles.title}
        </h2>
        {data.rolesImage && (
          <div
            className="mt-8 aspect-[16/9] rounded-2xl overflow-hidden border border-slate-200 shadow-sm bg-slate-100"
            data-testid={`industry-roles-image-${data.slug}`}
          >
            <img
              src={data.rolesImage.src}
              alt={data.rolesImage.alt}
              loading="lazy"
              className="w-full h-full object-cover"
            />
          </div>
        )}
        <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="industry-role-cards">
          {data.roles.cards.map((r, i) => (
            <article
              key={r.label}
              data-testid={`industry-role-${i}`}
              className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-[#4A6FA5] hover:shadow-md transition"
            >
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-[#F0F4F8] text-[#4A6FA5]">
                <Users className="w-5 h-5" strokeWidth={1.75} />
              </span>
              <h3 className="mt-4 font-heading text-lg text-slate-900">{r.label}</h3>
              <p className="mt-2 text-sm text-slate-700 leading-relaxed">{r.body}</p>
            </article>
          ))}
        </div>
      </section>

      {/* WHEN CHAPLAIN SUPPORT CAN HELP (optional) */}
      {data.whenChaplainHelps && (
        <section className="bg-white border-t border-slate-100">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <h2
              className="font-heading text-3xl sm:text-4xl tracking-tight text-slate-900"
              data-testid="industry-when-title"
            >
              {data.whenChaplainHelps.title}
            </h2>
            <ul
              className="mt-8 grid md:grid-cols-2 gap-3 max-w-4xl"
              data-testid="industry-when-examples"
            >
              {data.whenChaplainHelps.examples.map((ex, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4"
                  data-testid={`industry-when-example-${i}`}
                >
                  <Check className="w-4 h-4 text-[#4A6FA5] shrink-0 mt-1" strokeWidth={2} />
                  <span className="text-sm text-slate-700 leading-relaxed">{ex}</span>
                </li>
              ))}
            </ul>
            <p className="mt-6 text-xs text-slate-500 max-w-3xl">
              CCCHAPS chaplaincy is non-clinical and does not provide legal, financial, real-estate transaction, or mental-health treatment advice.
            </p>
          </div>
        </section>
      )}

      {/* WHAT'S INCLUDED */}
      <section className="bg-[#F8FAFC] border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-slate-900" data-testid="industry-included-title">
            Included with Every Care Team Package
          </h2>
          <p className="mt-3 text-slate-600 leading-relaxed max-w-3xl">
            Every CCCHAPS service arrangement — from a full workplace chaplaincy program to an on-call-only CALLACHAP arrangement — includes these baseline elements.
          </p>
          <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="industry-included-grid">
            <IncludedCard icon={HeartHandshake} title="24/7 On-Call Chaplain Access" body="A CCCHAPS chaplain is available around the clock. Response times vary by channel." />
            <IncludedCard icon={PhoneCall} title="CALLACHAP" body="Employee access through configured phone, text, confidential-message, and available remote-support channels." />
            <IncludedCard icon={Compass} title="CCCHAPS Care Portal" body="A private digital front door for employees to request confidential support and reach a chaplain." />
            <IncludedCard icon={PlayCircle} title="CCCHAPS On Demand" body="A digital library of practical, everyday resources available anytime, where provided by the organization." />
          </div>

          {/* Privacy line */}
          <div
            data-testid="industry-privacy-callout"
            className="mt-8 rounded-xl border border-slate-200 bg-white p-5 flex items-start gap-3 max-w-3xl"
          >
            <ShieldCheck className="w-5 h-5 text-[#4A6FA5] shrink-0 mt-0.5" strokeWidth={1.75} />
            <p className="text-sm text-slate-700 leading-relaxed">
              <span className="font-semibold text-slate-900">Confidentiality is standard.</span> Individual chaplain conversations are confidential within appropriate professional, ethical, safety, and legal limits. Organizations receive aggregate, non-identifying utilization information rather than the private details of individual conversations.
            </p>
          </div>
        </div>
      </section>

      {/* COMPLEMENTS EXISTING BENEFITS (optional) */}
      {data.complements && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2
            className="font-heading text-3xl sm:text-4xl tracking-tight text-slate-900"
            data-testid="industry-complements-title"
          >
            {data.complements.title}
          </h2>
          <p className="mt-4 text-slate-700 leading-relaxed max-w-3xl">
            {data.complements.body}
          </p>
        </section>
      )}

      {/* BUILT AROUND YOUR ORGANIZATION */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-slate-900" data-testid="industry-package-title">
              Built Around Your Organization
            </h2>
            <p className="mt-4 text-slate-700 leading-relaxed">
              CCCHAPS builds a Care Team Package sized to your operation — workforce size, department mix, locations, and the level of scheduled on-site, remote, or blended chaplain support based on the organization's Care Team Package.
            </p>
            <p className="mt-3 text-slate-700 leading-relaxed">
              Leadership consultation is available where appropriate, and organizational reporting is aggregate and non-identifying. CCCHAPS complements — it does not replace — your EAP, HR, medical, mental-health, legal, or emergency services.
            </p>
          </div>
          <ul className="rounded-2xl border border-slate-200 bg-white p-6 space-y-3 text-sm text-slate-700 leading-relaxed">
            {[
              "Scheduled on-site, remote, or blended chaplain presence based on your Care Team Package",
              "Confidential support for employees and their immediate household members",
              "Grief, family, relational, caregiving, stress, workplace, spiritual, and major-life-change support within chaplaincy scope",
              "Aggregate, non-identifying organizational reporting",
              "Leadership consultation where appropriate",
            ].map((line, i) => (
              <li key={i} className="flex items-start gap-3">
                <Check className="w-4 h-4 text-[#4A6FA5] shrink-0 mt-0.5" strokeWidth={2} />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="rounded-3xl bg-gradient-to-br from-[#3B597B] via-[#4A6FA5] to-[#4A6FA5] p-8 sm:p-12 text-white">
          <p className="text-xs uppercase tracking-[0.22em] text-blue-100 font-semibold mb-3">Next Step</p>
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight max-w-2xl">
            {data.closingCta?.title || "Ready to build a Care Team Package for your team?"}
          </h2>
          <p className="mt-4 text-blue-100 leading-relaxed max-w-2xl">
            {data.closingCta?.body || "A short conversation with our team is enough to shape a proposal that fits your workforce and locations. No pitch, no obligation."}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link
              to="/schedule?type=organizational_consultation"
              data-testid="industry-cta-primary"
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-white text-[#3B597B] font-semibold hover:bg-blue-50"
            >
              {data.closingCta?.buttonLabel || "Build a Care Team Package"} <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/packages"
              data-testid="industry-cta-secondary"
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg border border-white/40 text-white font-medium hover:bg-white/10"
            >
              View Care Team Packages
            </Link>
          </div>
          <p className="mt-6 text-xs text-blue-100 flex items-center gap-2" data-testid="industry-trust-line">
            <MessageSquare className="w-3.5 h-3.5" /> Confidential. Non-clinical. Relationship-based. Available 24/7 on-call.
          </p>
        </div>
      </section>
    </div>
  );
}

function IncludedCard({ icon: Icon, title, body }) {
  return (
    <article className="rounded-2xl border border-slate-200 bg-white p-6">
      <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-[#F0F4F8] text-[#4A6FA5]">
        <Icon className="w-5 h-5" strokeWidth={1.75} />
      </span>
      <h3 className="mt-4 font-heading text-base text-slate-900">{title}</h3>
      <p className="mt-2 text-sm text-slate-700 leading-relaxed">{body}</p>
    </article>
  );
}
